import React, { useState, useRef, useEffect } from 'react';
import type { OverseasShipment, Supplier, ShipmentItem } from '../../../types';
import XCircleIcon from '../../icons/XCircleIcon';
import UploadIcon from '../../icons/UploadIcon';
import PlusIcon from '../../icons/PlusIcon';
import SupplierModal from '../../purchasing/SupplierModal';
import { readFileAsText, importFromCSV, autoDetectColumns, validateImportedItems, type ImportedItem, type ColumnMapping } from '../../../lib/shipmentImportService';

interface CreateShipmentModalProps {
  suppliers: Supplier[];
  currentUserId: number;
  shipment?: OverseasShipment; // If provided, edit mode
  onClose: () => void;
  onSave: (shipment: Omit<OverseasShipment, 'id'>) => void;
  onUpdate?: (shipmentId: string, updates: Partial<OverseasShipment>) => void;
  onSaveSupplier?: (supplier: Supplier) => void;
}

type Step = 'basic' | 'import' | 'review';

const CreateShipmentModal: React.FC<CreateShipmentModalProps> = ({
  suppliers,
  currentUserId,
  shipment,
  onClose,
  onSave,
  onUpdate,
  onSaveSupplier,
}) => {
  const isEditMode = !!shipment;
  const [step, setStep] = useState<Step>(isEditMode ? 'import' : 'basic');
  const [formData, setFormData] = useState({
    shipmentNumber: shipment?.shipmentNumber || '',
    supplierId: shipment?.supplierId || 0,
    expectedArrivalDate: shipment?.expectedArrivalDate ? new Date(shipment.expectedArrivalDate).toISOString().split('T')[0] : '',
    notes: shipment?.notes || '',
  });
  const [importedItems, setImportedItems] = useState<ImportedItem[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    name: '',
    quantity: '',
  });
  const [csvContent, setCsvContent] = useState<string>('');
  const [hasHeader, setHasHeader] = useState(true);
  const [importError, setImportError] = useState<string>('');
  const [validationResult, setValidationResult] = useState<{ valid: ImportedItem[]; invalid: { item: ImportedItem; reason: string }[] } | null>(null);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [localSuppliers, setLocalSuppliers] = useState<Supplier[]>(suppliers);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update localSuppliers when suppliers prop changes
  useEffect(() => {
    setLocalSuppliers(suppliers);
  }, [suppliers]);

  const handleBasicInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'supplierId' ? parseInt(value) : value,
    }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.txt')) {
      setImportError('Please upload a CSV file');
      return;
    }

    try {
      const content = await readFileAsText(file);
      setCsvContent(content);
      setImportError('');

      // Auto-detect columns if header exists
      const lines = content.split('\n');
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        const mapping = autoDetectColumns(headers);
        setColumnMapping(mapping);
      }
    } catch (error) {
      setImportError('Failed to read file. Please try again.');
      console.error('File read error:', error);
    }
  };

  const handleImport = () => {
    if (!csvContent) {
      setImportError('Please upload a CSV file first');
      return;
    }

    try {
      const items = importFromCSV(csvContent, columnMapping, hasHeader);
      setImportedItems(items);
      setImportError('');

      // Validate items
      const validation = validateImportedItems(items);
      setValidationResult(validation);

      if (validation.valid.length > 0) {
        setStep('review');
      } else {
        setImportError('No valid items found. Please check your CSV file.');
      }
    } catch (error: any) {
      setImportError(error.message || 'Failed to import CSV. Please check the format.');
      console.error('Import error:', error);
    }
  };

  const handleSave = () => {
    if (!formData.shipmentNumber || !formData.supplierId) {
      alert('Please fill in all required fields');
      return;
    }

    if (isEditMode && shipment) {
      // Edit mode: Append new items to existing shipment
      if (!validationResult || validationResult.valid.length === 0) {
        alert('Please import at least one item to add');
        return;
      }

      if (!onUpdate) {
        alert('Update function not available');
        return;
      }

      const supplier = localSuppliers.find(s => s.id === formData.supplierId);
      const newItems: ShipmentItem[] = validationResult.valid.map((item, index) => ({
        id: `item-${Date.now()}-${index}`,
        shipmentId: shipment.id,
        expectedName: item.name,
        expectedQuantity: item.quantity,
        expectedUnit: item.unit,
        expectedCost: item.cost,
        expectedTotalAmount: item.totalAmount,
        expectedSKU: item.sku,
        expectedSerialNumber: item.serialNumber,
        expectedDescription: item.description,
        receivingStatus: 'pending',
      }));

      // Merge with existing items
      const allItems = [...(shipment.items || []), ...newItems];
      const totalExpectedValue = allItems.reduce((sum, item) => 
        sum + (item.expectedQuantity * (item.expectedCost || 0)), 0
      );

      onUpdate(shipment.id, {
        items: allItems,
        totalExpectedValue,
        supplierName: supplier?.name,
        notes: formData.notes || undefined,
      });
    } else {
      // Create mode: Create new shipment
      if (importedItems.length === 0) {
        alert('Please import at least one item');
        return;
      }

      const supplier = localSuppliers.find(s => s.id === formData.supplierId);
      const shipmentItems: ShipmentItem[] = validationResult?.valid.map((item, index) => ({
        id: `item-${Date.now()}-${index}`,
        shipmentId: '', // Will be set after shipment is created
        expectedName: item.name,
        expectedQuantity: item.quantity,
        expectedUnit: item.unit,
        expectedCost: item.cost, // Rate/Unit
        expectedTotalAmount: item.totalAmount, // Total Amount
        expectedSKU: item.sku,
        expectedSerialNumber: item.serialNumber,
        expectedDescription: item.description,
        receivingStatus: 'pending',
      })) || [];

      const newShipment: Omit<OverseasShipment, 'id'> = {
        shipmentNumber: formData.shipmentNumber,
        supplierId: formData.supplierId,
        supplierName: supplier?.name,
        expectedArrivalDate: formData.expectedArrivalDate ? new Date(formData.expectedArrivalDate).getTime() : undefined,
        status: 'pending',
        dateCreated: Date.now(),
        createdBy: currentUserId,
        notes: formData.notes || undefined,
        items: shipmentItems,
        totalExpectedValue: shipmentItems.reduce((sum, item) => 
          sum + (item.expectedQuantity * (item.expectedCost || 0)), 0
        ),
      };

      onSave(newShipment);
    }
  };

  const canProceedToImport = formData.shipmentNumber && formData.supplierId;
  const canProceedToReview = importedItems.length > 0 && validationResult && validationResult.valid.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {isEditMode ? 'Edit Manifest / Add Items' : 'Create Overseas Shipment Manifest'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${step === 'basic' ? 'text-pink-600' : step === 'import' || step === 'review' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'basic' ? 'bg-pink-100' : 'bg-green-100'}`}>
                1
              </div>
              <span className="ml-2 font-medium">Basic Info</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step === 'import' || step === 'review' ? 'bg-green-500' : 'bg-gray-300'}`} />
            <div className={`flex items-center ${step === 'import' ? 'text-pink-600' : step === 'review' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'import' ? 'bg-pink-100' : step === 'review' ? 'bg-green-100' : 'bg-gray-100'}`}>
                2
              </div>
              <span className="ml-2 font-medium">Import Items</span>
            </div>
            <div className={`flex-1 h-1 mx-2 ${step === 'review' ? 'bg-green-500' : 'bg-gray-300'}`} />
            <div className={`flex items-center ${step === 'review' ? 'text-pink-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'review' ? 'bg-pink-100' : 'bg-gray-100'}`}>
                3
              </div>
              <span className="ml-2 font-medium">Review</span>
            </div>
          </div>
        </div>

        {/* Step 1: Basic Info */}
        {step === 'basic' && (
          <div className="space-y-4">
            {isEditMode && shipment && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>Editing:</strong> {shipment.shipmentNumber} - {shipment.items?.length || 0} existing items
                </p>
                <p className="text-blue-700 text-xs mt-1">
                  New items will be added to this manifest. You can skip to the import step to add items.
                </p>
              </div>
            )}
            <div>
              <label htmlFor="shipmentNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Shipment Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="shipmentNumber"
                name="shipmentNumber"
                value={formData.shipmentNumber}
                onChange={handleBasicInfoChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="e.g., PO-2026-001"
                required
                disabled={isEditMode}
              />
              {isEditMode && (
                <p className="text-xs text-gray-500 mt-1">Shipment number cannot be changed</p>
              )}
            </div>

            <div>
              <label htmlFor="supplierId" className="block text-sm font-medium text-gray-700 mb-1">
                Supplier <span className="text-red-500">*</span>
              </label>
              <div className="flex space-x-2">
                <select
                  id="supplierId"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={handleBasicInfoChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                  required
                >
                  <option value={0}>Select a supplier</option>
                  {localSuppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setIsSupplierModalOpen(true)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 flex items-center"
                  title="Add New Supplier"
                  disabled={!onSaveSupplier}
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="expectedArrivalDate" className="block text-sm font-medium text-gray-700 mb-1">
                Expected Arrival Date
              </label>
              <input
                type="date"
                id="expectedArrivalDate"
                name="expectedArrivalDate"
                value={formData.expectedArrivalDate}
                onChange={handleBasicInfoChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
              />
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleBasicInfoChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-pink-500 focus:border-pink-500"
                placeholder="Additional notes about this shipment..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('import')}
                disabled={!canProceedToImport}
                className={`px-4 py-2 rounded-md text-white ${
                  canProceedToImport
                    ? 'bg-pink-500 hover:bg-pink-600'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Next: Import Items
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Import Items */}
        {step === 'import' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload CSV File
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <UploadIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-pink-500 text-white px-4 py-2 rounded-md hover:bg-pink-600 mb-2"
                >
                  Choose File
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  Upload a CSV file with item names, quantities, and other details
                </p>
              </div>
            </div>

            {csvContent && (
              <div className="space-y-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={hasHeader}
                      onChange={(e) => setHasHeader(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">CSV file has header row</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number Column</label>
                    <input
                      type="text"
                      value={columnMapping.serialNumber || ''}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, serialNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Serial Number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description Column</label>
                    <input
                      type="text"
                      value={columnMapping.description || ''}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Column <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={columnMapping.quantity}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, quantity: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Quantity"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate/Unit Column</label>
                    <input
                      type="text"
                      value={columnMapping.cost || ''}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, cost: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Rate/Unit"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Amount Column</label>
                    <input
                      type="text"
                      value={columnMapping.totalAmount || ''}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, totalAmount: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Total Amount"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name Column (if different from Description)</label>
                    <input
                      type="text"
                      value={columnMapping.name}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Item Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit Column (optional)</label>
                    <input
                      type="text"
                      value={columnMapping.unit || ''}
                      onChange={(e) => setColumnMapping(prev => ({ ...prev, unit: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Unit"
                    />
                  </div>
                </div>

                {importError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {importError}
                  </div>
                )}

                <button
                  onClick={handleImport}
                  className="w-full bg-pink-500 text-white px-4 py-2 rounded-md hover:bg-pink-600"
                >
                  Import Items
                </button>
              </div>
            )}

            {validationResult && (
              <div className="mt-4">
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-2">
                  {validationResult.valid.length} valid items imported
                </div>
                {validationResult.invalid.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
                    {validationResult.invalid.length} items skipped: {validationResult.invalid.map(i => i.reason).join(', ')}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setStep('basic')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={() => setStep('review')}
                disabled={!canProceedToReview}
                className={`px-4 py-2 rounded-md text-white ${
                  canProceedToReview
                    ? 'bg-pink-500 hover:bg-pink-600'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Next: Review
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Shipment Summary</h3>
              <p><strong>Shipment Number:</strong> {formData.shipmentNumber}</p>
              <p><strong>Supplier:</strong> {localSuppliers.find(s => s.id === formData.supplierId)?.name}</p>
              <p><strong>New Items to Add:</strong> {validationResult?.valid.length || 0}</p>
              {isEditMode && shipment && (
                <p><strong>Existing Items:</strong> {shipment.items?.length || 0}</p>
              )}
              {isEditMode && shipment && validationResult && (
                <p><strong>Total Items After Adding:</strong> {(shipment.items?.length || 0) + (validationResult.valid.length || 0)}</p>
              )}
            </div>

            {isEditMode && shipment && shipment.items && shipment.items.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Existing Items ({shipment.items.length})</h4>
                <div className="max-h-48 overflow-y-auto">
                  <ul className="text-sm text-blue-700 space-y-1">
                    {shipment.items.map((item, idx) => (
                      <li key={idx}>
                        {item.expectedDescription || item.expectedName} - {item.expectedQuantity} {item.expectedUnit || 'units'}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">
                {isEditMode ? 'New Items to Add' : 'Items to Import'}
              </h4>
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Serial #</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate/Unit</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {validationResult?.valid.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm">{item.serialNumber || 'N/A'}</td>
                        <td className="px-4 py-2 text-sm">{item.description || item.name}</td>
                        <td className="px-4 py-2 text-sm">{item.quantity}</td>
                        <td className="px-4 py-2 text-sm">{item.cost ? `$${item.cost.toFixed(2)}` : 'N/A'}</td>
                        <td className="px-4 py-2 text-sm">{item.totalAmount ? `$${item.totalAmount.toFixed(2)}` : 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={() => setStep('import')}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
              >
                {isEditMode ? 'Add Items to Manifest' : 'Create Manifest'}
              </button>
            </div>
          </div>
        )}
      </div>
      {isSupplierModalOpen && (
        <SupplierModal
          supplier={null}
          onClose={() => setIsSupplierModalOpen(false)}
          onSave={(supplier) => {
            if (onSaveSupplier) {
              const newSupplier = { ...supplier, id: Date.now() };
              onSaveSupplier(newSupplier);
              setLocalSuppliers(prev => [...prev, newSupplier]);
              setFormData(prev => ({ ...prev, supplierId: newSupplier.id }));
            }
            setIsSupplierModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CreateShipmentModal;
